import { Component, inject, Input, signal, computed, OnInit, OnDestroy } from '@angular/core'
import { ChatService } from '../../services/chat.service';
import { NgIf, NgClass, CommonModule, } from '@angular/common';
import { IReaction, MessageType } from '../../enum/constants.enum';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IMessageList } from '../../interfaces/types.interface'
import { MatTooltipModule } from '@angular/material/tooltip';
import { MarkdownComponent, MarkdownService } from 'ngx-markdown';
import { ThinkingUiComponent } from '../thinking-ui/thinking-ui.component';
import { HttpClient } from '@angular/common/http'
import { CreateFeedBackPayload } from '../../interfaces/feedback'
import { concatMap, finalize, from, map, timer, Observable } from 'rxjs'
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from '../../../environments/environment';
import { PinnedQueryService } from '../../services/pinned-query.service';


@Component({
    selector: 'fyxt-chat-message',
    templateUrl: './chat-message.component.html',
    styleUrls: ['./chat-message.component.scss'],
    imports: [
      MatFormFieldModule,
      MatInputModule,
      MatButtonModule,
      MatIconModule,
      NgIf,
      NgClass,
      FormsModule,
      CommonModule,
      MatTooltipModule,
      MarkdownComponent,
      MatProgressSpinnerModule,
      ThinkingUiComponent,
    ],
    animations: [
      trigger('fadeInOut', [
        transition(':enter', [
          style({ opacity: 0 }),
          animate('300ms ease-in', style({ opacity: 1 }))
        ]),
        transition(':leave', [
          animate('300ms ease-out', style({ opacity: 0 }))
        ])
      ])
    ]
})
export class ChatMessageComponent implements OnInit, OnDestroy {
    private snackBar = inject(MatSnackBar);
    readonly pinnedQueryService = inject(PinnedQueryService);
    isCsvLoading = signal(false)
    /** Gap between consecutive downloads so the browser doesn't drop them */
    private readonly downloadStaggerMs = 500;
    isFeedbackSubmitting = signal<IReaction | null>(null)
    isPinSubmitting = signal(false)
    showFeedbackForm = signal(false)
    thankYouMessageVisible = signal(false)
    /** Collapsed/expanded state for the AI reasoning panel. */
    reasoningExpanded = signal(false)
    /** Whether this message is currently streaming tokens (approximation). */
    isReasoningStreaming = signal(false)
    /** Duration in seconds that the assistant has been "thinking" for this message. */
    reasoningDuration = signal<number | undefined>(undefined)
    /** Animated dots shown at the end of thinking text while streaming. */
    thinkingDots = signal<string>('')
    private reasoningStartTime: number | null = null
    private reasoningTimerId: any = null
    private hasAutoClosedReasoning = false
    private thinkingDotsStep = 0
    readonly showReasoningLogs = environment.showReasoningLogs ?? false;
    public type = MessageType;
    public reactions = IReaction;
    @Input() public message!: IMessageList

    isPinned = computed(() =>
      this.pinnedQueryService.isPinned(this.message?.id, this.message?.text)
    );

    toggleReasoning(): void {
      this.reasoningExpanded.update(v => !v);
    }

    togglePin(): void {
      if (
        this.isPinSubmitting() ||
        this.pinnedQueryService.isMutating() ||
        !this.message?.text?.trim()
      ) {
        return;
      }

      const existing = this.pinnedQueryService.findPinnedForMessage(
        this.message.id,
        this.message.text,
      );

      this.isPinSubmitting.set(true);

      // Unify pin/unpin Observable types so .pipe/.subscribe type-check.
      const request$: Observable<unknown> = existing
        ? this.pinnedQueryService.unpinQuery(existing.id)
        : this.pinnedQueryService.pinQuery({
            text: this.message.text.trim(),
            message_id: this.message.id || undefined,
            session_id: this.message.session_id || this.chatService.currentSessionId,
          });

      request$
        .pipe(finalize(() => this.isPinSubmitting.set(false)))
        .subscribe({
          error: (error: any) => {
            if (existing) {
              this.snackBar.open('Failed to unpin question. Please try again.', 'Close', {
                duration: 5000,
                panelClass: ['error-snackbar'],
              });
              return;
            }
            const rawMessage =
              error?.error?.errors?.[0]?.message ||
              error?.error?.message ||
              error?.message;
            const apiMessage = Array.isArray(rawMessage)
              ? rawMessage[0]
              : rawMessage;
            const status = error?.status;
            let message = 'Failed to pin question. Please try again.';
            if (status === 409) {
              message = 'Question is already pinned';
            } else if ((status === 400 || status === 401) && apiMessage) {
              message = apiMessage;
            }
            this.snackBar.open(message, 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar'],
            });
          },
        });
    }

    constructor(
        public chatService: ChatService,
        public markdownService: MarkdownService,
        private readonly _http: HttpClient,
    ) {}

    ngOnInit() {
      if (this.message.isReaction && this.message.reaction === 'bad') {
        this.showFeedbackForm.set(true)
      }

      // If this is the latest AI message and the chat is currently streaming, treat reasoning as streaming.
      this.updateStreamingState()
      if (this.isReasoningStreaming()) {
        this.reasoningExpanded.set(true)
        this.startReasoningTimer()
      }
    }

    ngOnDestroy() {
      if (this.reasoningTimerId) {
        clearInterval(this.reasoningTimerId)
        this.reasoningTimerId = null
      }
    }

    private updateStreamingState(): void {
      const messages = this.chatService.messagesSubject.getValue()
      const last = messages[messages.length - 1]
      const isLastAi =
        !!last && last.id === this.message.id && this.message.sender === this.type.MACHINE
      // Use chatService.isLoading as a proxy for "streaming in progress"
      this.isReasoningStreaming.set(isLastAi && this.chatService.isLoading)
    }

    private startReasoningTimer(): void {
      if (this.reasoningStartTime !== null) return
      this.reasoningStartTime = Date.now()
      this.reasoningDuration.set(0)
      this.thinkingDots.set('.')
      this.thinkingDotsStep = 1
      this.reasoningTimerId = setInterval(() => {
        if (!this.isReasoningStreaming()) {
          // Streaming has finished; finalize duration and auto-close once.
          if (this.reasoningStartTime !== null) {
            const seconds = Math.ceil((Date.now() - this.reasoningStartTime) / 1000)
            this.reasoningDuration.set(seconds)
          }
          this.reasoningStartTime = null
          // Clear dots when streaming ends
          this.thinkingDots.set('')
          this.thinkingDotsStep = 0
          if (!this.hasAutoClosedReasoning) {
            this.hasAutoClosedReasoning = true
            setTimeout(() => {
              this.reasoningExpanded.set(false)
            }, 1000)
          }
          if (this.reasoningTimerId) {
            clearInterval(this.reasoningTimerId)
            this.reasoningTimerId = null
          }
          return
        }
        if (this.reasoningStartTime !== null) {
          const seconds = Math.ceil((Date.now() - this.reasoningStartTime) / 1000)
          this.reasoningDuration.set(seconds)
          // Update animated dots: ".", "..", "..." then repeat
          this.thinkingDotsStep = (this.thinkingDotsStep % 3) + 1
          this.thinkingDots.set('.'.repeat(this.thinkingDotsStep))
        }
      }, 1000)
    }

    onThumbUp(messageId: string) {
      this.showFeedbackForm.set(false)

      if (this.isFeedbackSubmitting()) {
        return;
      }

      if (this.message.reaction === this.reactions.good && this.message.feedbackId) {
        this.deleteGoodFeedBack(this.message.feedbackId, this.reactions.good)
        return
      }

      this.createPositiveFeedBack(messageId);
    }

    deleteGoodFeedBack(feedbackId: string, currentReaction: IReaction) {
      this.isFeedbackSubmitting.set(currentReaction);
      this.chatService.deleteFeedback(feedbackId).pipe(
        finalize(() => {
          this.isFeedbackSubmitting.set(null);
        })
      ).subscribe({
        next: () => {
          const currentMessages = this.chatService.messagesSubject.getValue()
          const currentMessageIndex = currentMessages.findIndex((m) => m.id === this.message.id);
          const currentMessage = currentMessages[currentMessageIndex];
          currentMessages[currentMessageIndex] = { ...currentMessage, feedbackId: '', reaction: '', isReaction: false }
        },
        error: () => {
          this.message.reaction = currentReaction;
          this.message.isReaction = true;
        }
      })
    }

    deleteBadFeedBack(feedbackId: string, currentReaction: IReaction) {
      this.isFeedbackSubmitting.set(currentReaction);
      this.chatService.deleteFeedback(feedbackId).pipe(
        finalize(() => {
          this.isFeedbackSubmitting.set(null);
          this.showFeedbackForm.set(false)
        })
      ).subscribe({
        next: () => {
          const currentMessages = this.chatService.messagesSubject.getValue()
          const currentMessageIndex = currentMessages.findIndex((m) => m.id === this.message.id);
          const currentMessage = currentMessages[currentMessageIndex];
          currentMessages[currentMessageIndex] = { ...currentMessage, feedbackId: '', reaction: '', isReaction: false }
        },
        error: () => {
          this.message.reaction = currentReaction;
          this.message.isReaction = true;
        }
      })
    }

    onThumbDown() {
        if (this.isFeedbackSubmitting()) {
          return;
        }

        if (!this.showFeedbackForm()) {
          this.showFeedbackForm.set(true)
        } else if (this.showFeedbackForm && !this.message.feedbackId) {
          this.showFeedbackForm.set(false)
        } else {
          if (this.showFeedbackForm() && this.message.reaction === this.reactions.bad && this.message.feedbackId) {
            this.deleteBadFeedBack(this.message.feedbackId, this.reactions.bad)
            return
          }
        }
    }

    createPositiveFeedBack(messageId: string, comment?: string) {
      this.message.reaction = this.reactions.good;
      this.message.isReaction = true;
      this.isFeedbackSubmitting.set(this.reactions.good);

        const payload: CreateFeedBackPayload = {
            message_id: messageId,
            score: 5,
            comment,
        }
        this.chatService.upsertFeedBack(payload)
          .pipe(
            map((r) => r.data),
            finalize(() => {
              this.isFeedbackSubmitting.set(null);
            })
          )
            .subscribe({
              next: (res) => {
                this.message.feedbackId = res.id
              },
              error: (err) => {
                delete this.message?.comment
                delete this.message.reaction
              },
            })
    }

    showThankYouMessage() {
      this.thankYouMessageVisible.set(true);

      // Automatically hide the thank you message after 5 seconds
      setTimeout(() => {
        this.thankYouMessageVisible.set(false);
      }, 5000);
    }

    sendReaction(messageId: string) {
        // If already submitting, don't allow multiple submissions
        if (this.isFeedbackSubmitting()) {
            return;
        }

        const score = 1;
        const userFeedback = this.message?.comment || '';

        console.log('Starting feedback submission...');
        // Set loading state to true
        this.isFeedbackSubmitting.set(this.reactions.bad);

        // Add a small delay to ensure the UI updates before the request
        setTimeout(() => {
            this.chatService.upsertFeedBack({
                message_id: messageId,
                score,
                comment: userFeedback
            }).pipe(
                finalize(() => {
                    console.log('Feedback submission complete');
                    // Set loading state to false when request completes (success or error)
                    this.isFeedbackSubmitting.set(null);
                    // this.showFeedbackForm.set(false)
                })
            ).subscribe({
                next: (response) => {
                    console.log('Feedback submitted successfully');
                    this.showThankYouMessage()
                    this.message.feedbackId = response.data.id;
                    this.message.reaction = this.reactions.bad;
                    this.message.userFeedback = userFeedback;

                    // Store the comment in userFeedback but don't delete it
                    // We don't delete this.message.comment to keep the input visible
                },
                error: (error) => {
                    console.error('Feedback submission failed', error);
                    // Don't delete the comment to keep the input visible
                    // Only reset the reaction if needed
                    // delete this.message.reaction;

                    // Show error message
                    this.snackBar.open('Failed to submit feedback. Please try again.', 'Close', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                }
            });
        }, 10); // Small delay to ensure UI updates
    }

    downloadAllFiles() {
      if (this.isCsvLoading()) return;

      this.isCsvLoading.set(true);

      // Send PATCH API call asynchronously (fire-and-forget) to track CSV download
      if (this.message.id) {
        this.chatService.patchCsvDownload(this.message.id).subscribe({
          next: () => {},
          error: (error) => {
            console.error('Failed to track CSV download:', error);
          }
        });
      }

      // Download all files from download_urls, or fall back to single download_url
      const urls = this.message.download_urls?.length
        ? this.message.download_urls.map((dl) => dl.csv_link)
        : this.message.download_url
          ? [this.message.download_url]
          : [];

      if (!urls.length) {
        this.isCsvLoading.set(false);
        return;
      }

      // Downloads run one after another with a gap between them - the browser
      // swallows downloads that are triggered back to back in the same tick
      from(urls)
        .pipe(
          concatMap((url, index) =>
            index === 0
              ? this.chatService.downloadCsv(url)
              : timer(this.downloadStaggerMs).pipe(concatMap(() => this.chatService.downloadCsv(url)))
          ),
          finalize(() => this.isCsvLoading.set(false))
        )
        .subscribe({
          error: (error) => {
            console.error('Failed to download files:', error);
            this.snackBar.open('Failed to download the file. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar']
            });
          }
        });
    }

    jsonParse(message: string | undefined) : any {
      if (!message) return null;
      try {
        return JSON.parse(message || '{}');
      } catch (e) {
        return null;
      }
    }

  getThinkingThoughts(message: IMessageList): string[] {
    const liveSteps = message.thinkingSteps || [];
    if (liveSteps.length) {
      return liveSteps;
    }
    if (message.reasoning) {
      return message.reasoning
        .split('\n')
        .map((t) => t.trim())
        .filter((t) => !!t);
    }
    return [];
  }
}
